// TES-1070 (TES-1069 S1) — when the patient page offers the doctor's
// prescription step, and what makes the button pressable. Pure, so
// `node --test` can run it (see test/treatment-start.test.ts).
//
// TES-1150 (Emelie 2026-10-07) — the step is "Bekräfta receptstatus", taken
// in the same session as the decision and BEFORE the subscription. It
// registers the prescription; the verified subscription releases it to the
// patient.
import type {
  SubscriptionStanding,
  TreatmentStartState,
} from "@care-by-ella/contracts";

/** The anchor on the patient page; the work item links here. */
export const TREATMENT_START_ANCHOR = "behandlingsstart";

// The states in which the doctor's step is open: a published positive
// decision whose prescription is not registered yet, paid or not. Never in
// the admin's read-only support view (TES-1040).
const REGISTRABLE: ReadonlySet<TreatmentStartState> = new Set([
  "MEDICAL_DECISION_APPROVED",
  "AWAITING_SUBSCRIPTION",
  "SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION",
]);

export function showsConfirmPrescription(
  state: TreatmentStartState | null | undefined,
  readOnly: boolean,
): boolean {
  return !readOnly && !!state && REGISTRABLE.has(state);
}

// The step is open but this viewer cannot act on it (the read-only support
// view): it is shown as "not yet confirmed", without controls.
export function showsPendingPrescriptionStep(
  state: TreatmentStartState | null | undefined,
): boolean {
  return !!state && REGISTRABLE.has(state);
}

// TES-1150 — after the doctor's registration, until the verified
// subscription releases it: "Recept utfärdat – inväntar
// abonnemangsaktivering". Information only; nothing is left for the doctor.
export function showsAwaitingActivation(
  state: TreatmentStartState | null | undefined,
): boolean {
  return state === "PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION";
}

export const PRESCRIPTION_STEP_HINT =
  "Patienten ser receptet först när abonnemanget är aktiverat. Du behöver inte återvända till ärendet efter betalningen.";

export const AWAITING_ACTIVATION_HINT =
  "Läkarens del är klar. När abonnemanget är verifierat aktiverat visas receptet för patienten och uthämtningen skapas automatiskt.";

// A prescription choice ("Nytt recept är utfärdat i Webdoc" or
// "Befintligt recept gäller fortfarande"), and not while a submit is running.
// TES-1150: the separate prescription note and its Webdoc tick are gone.
export function canConfirmPrescription(input: {
  choice: "new_issued" | "existing_valid" | null;
  busy: boolean;
}): boolean {
  return input.choice !== null && !input.busy;
}

/**
 * TES-1095 (Emelie, TES-1093 rule 6) — the link re-send is offered only
 * while she has not subscribed: once she has, the subscription cannot be
 * changed or taken out again while she waits for the prescription (the API
 * refuses it too). Unknown standing (an older server) keeps the control.
 */
export function offersSubscriptionLinkResend(
  standing: SubscriptionStanding | null | undefined,
): boolean {
  return (
    standing !== "active_with_coach" && standing !== "active_without_coach"
  );
}
